import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import Select from "react-select";
import SearchableSelect from "../../SearchableSelect";
import useSelectedDomain from "../../service/Hooks/useSelectedDomain";
import { useLoader } from "../../service/Hooks/useLoader";
import {
  getSecuritiesByDomainMonetary,
  getSecuritiesByDomain,
  getSecuritiesByDomains,
} from "../../service/api/SecurityService";
import { createEvent, updateEvent } from "../../service/api/eventService";

// ============================================================================
// CONFIGURATION & CONSTANTES
// ============================================================================

const EVENT_TYPES = [
  { value: "Dividend to be paid", label: "Dividend to be paid" },
  { value: "Capital repayment", label: "Capital repayment" },
  { value: "Non Cash", label: "Non Cash" },
];

const SECURITY_TYPES = [
  { key: "private", label: "Titres Privés" },
  { key: "public", label: "Titres Publics" },
  { key: "monetary", label: "Titres Monétaires" },
];

const INITIAL_FORM_STATE = {
  type: "",
  amount: "",
  capital: "",
  nombreTitre: "",
  securityId: "",
  dateDePlanification: "",
};

// ============================================================================
// COMPOSANTS DE CHAMPS
// ============================================================================

const EventTypeSelector = ({ value, onChange, error }) => (
  <div className="mb-3">
    <label className="form-label">Type OST</label>
    <Select
      options={EVENT_TYPES}
      value={EVENT_TYPES.find(opt => opt.value === value) || null}
      onChange={selected => onChange(selected?.value || "")}
      classNamePrefix="react-select"
      className={error ? "is-invalid" : ""}
      placeholder="Sélectionner un type"
    />
    {value && (
      <div className="form-text text-muted mt-1">
        {value === "Dividend to be paid" && "💰 Le champ Montant sera affiché"}
        {value === "Capital repayment" && "📊 Le champ Capital sera affiché"}
        {value === "Non Cash" && "🔢 Le champ Nombre de titres sera affiché"}
      </div>
    )}
    {error && <div className="invalid-feedback d-block">{error}</div>}
  </div>
);

const ConditionalInput = ({ type, label, name, value, onChange, error, icon, hint, min, max, step = "0.01" }) => (
  <div className="mb-3">
    <label className="form-label">
      {label} {hint && <span className="text-muted">{hint}</span>}
    </label>
    <div className="input-group">
      <input
        type="number"
        name={name}
        value={value}
        onChange={onChange}
        className={`form-control ${error ? "is-invalid" : ""}`}
        min={min}
        max={max}
        step={step}
        placeholder={`Ex: ${type === "nombreTitre" ? "0.25" : type === "capital" ? "25" : "1000"}`}
      />
      {icon && (
        <span className="input-group-text">
          <i className={`fas fa-${icon}`}></i>
        </span>
      )}
    </div>
    {error && <div className="invalid-feedback d-block">{error}</div>}
  </div>
);

const SecurityTypeButtons = ({ value, onChange }) => (
  <div className="mb-3">
    <div className="btn-group gap-2 w-100" role="group">
      {SECURITY_TYPES.map(({ key, label }) => (
        <button
          key={key}
          type="button"
          className={`btn ${value === key ? "btn-primary" : "btn-outline-primary"}`}
          onClick={() => onChange(key)}
        >
          {label}
        </button>
      ))}
    </div>
  </div>
);

const SecuritySelector = ({ securities, value, onChange, error, securityType }) => (
  <div className="mb-3">
    <label className="form-label">
      Titres {securityType === "private" ? "(Privés)" : securityType === "public" ? "(Publics)" : "(Monétaires)"}
    </label>
    <SearchableSelect
      options={securities}
      value={value}
      onChange={onChange}
      labelKey="name"
      valueKey="id"
      searchKeys={["name"]}
      placeholder="Sélectionner un titre"
      className={`form-select form-control-sm fs-4 ${error ? "is-invalid" : ""}`}
    />
    {error && <div className="invalid-feedback">{error}</div>}
  </div>
);

const DateInput = ({ value, onChange, error }) => (
  <div className="mb-3">
    <label className="form-label">Date Planification</label>
    <input
      type="date"
      name="dateDePlanification"
      value={value}
      onChange={onChange}
      className={`form-control ${error ? "is-invalid" : ""}`}
      min={new Date().toISOString().split('T')[0]}
    />
    {error && <div className="invalid-feedback">{error}</div>}
  </div>
);

// ============================================================================
// HOOKS PERSONNALISÉS
// ============================================================================

const useSecurities = (securityType, domainId) => {
  const [securities, setSecurities] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!domainId) return;

    const fetchSecurities = async () => {
      setLoading(true);
      try {
        const params = { domainId, pageNumber: 1, pageSize: 1000 };
        let data;

        switch (securityType) {
          case "private":
            data = await getSecuritiesByDomains(params);
            break;
          case "monetary":
            data = await getSecuritiesByDomainMonetary(params);
            break;
          default:
            data = await getSecuritiesByDomain(params);
        }

        setSecurities(data.items || []);
      } catch  {
        // toast.error("Erreur lors du chargement des titres");
        setSecurities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchSecurities();
  }, [securityType, domainId]);

  return { securities, loading };
};

const useFormValidation = (formData) => {
  const [errors, setErrors] = useState({});

  const validateField = useCallback((name, value) => {
    let error = "";

    switch (name) {
      case "type":
        if (!value) error = "Le type d'événement est requis.";
        break;

      case "amount":
        if (formData.type === "Dividend to be paid") {
          if (!value) error = "Le montant est requis.";
          else if (isNaN(value) || parseFloat(value) <= 0) error = "Montant invalide.";
        }
        break;

      case "capital":
        if (formData.type === "Capital repayment") {
          if (!value) error = "Le capital est requis.";
          else if (isNaN(value) || parseFloat(value) <= 0 || parseFloat(value) > 100) {
            error = "Le capital doit être entre 0 et 100%.";
          }
        }
        break;

      case "nombreTitre":
        if (formData.type === "Non Cash") {
          const val = parseFloat(value);
          if (value === "") error = "Le nombre de titres est requis.";
          else if (isNaN(val) || val <= 0 || val > 1) {
            error = "Le nombre doit être entre 0 et 1.";
          }
        }
        break;

      case "securityId":
        if (!value) error = "La sélection d'un titre est requise.";
        break;

      case "dateDePlanification":
        if (!value) {
          error = "La date est requise.";
        } else {
          const selected = new Date(value);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (selected < today) error = "La date ne peut pas être dans le passé.";
        }
        break;
    }

    setErrors(prev => ({ ...prev, [name]: error }));
    return error === "";
  }, [formData.type]);

  const validateAll = useCallback(() => {
    const fieldsToValidate = ["type", "securityId", "dateDePlanification"];
    
    if (formData.type === "Dividend to be paid") fieldsToValidate.push("amount");
    if (formData.type === "Capital repayment") fieldsToValidate.push("capital");
    if (formData.type === "Non Cash") fieldsToValidate.push("nombreTitre");

    const results = fieldsToValidate.map(field => validateField(field, formData[field]));
    return results.every(Boolean);
  }, [formData, validateField]);

  return { errors, validateField, validateAll, setErrors };
};

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

const FormulaireEvenement = ({ editEvent, onEventCreated, onEventUpdated }) => {
  const selectedDomain = useSelectedDomain();
  const { showLoader, hideLoader } = useLoader();
  
  const [securityType, setSecurityType] = useState("public");
  const [formData, setFormData] = useState({
    ...INITIAL_FORM_STATE,
    domainId: selectedDomain?.id || "",
  });
  const [loading, setLoading] = useState(false);

  const { securities } = useSecurities(securityType, selectedDomain?.id);
  const { errors, validateField, validateAll, setErrors } = useFormValidation(formData);

  // Déterminer si c'est une édition en temps réel
  const isEditMode = useMemo(() => !!editEvent, [editEvent]);

  // Réinitialisation complète du formulaire
  const resetForm = useCallback(() => {
    setFormData({
      ...INITIAL_FORM_STATE,
      domainId: selectedDomain?.id || "",
    });
    setErrors({});
    setSecurityType("public");
  }, [selectedDomain, setErrors]);

  // Initialisation pour l'édition
  useEffect(() => {
    if (editEvent) {
      const matchingSecurity = securities.find(s => s.name === editEvent.securityName);
      setFormData({
        type: editEvent.type || "",
        amount: editEvent.amount || "",
        capital: editEvent.capital || "",
        nombreTitre: editEvent.nombreTitre || "",
        securityId: matchingSecurity?.id || "",
        dateDePlanification: editEvent.dateDePlanification
          ? new Date(editEvent.dateDePlanification).toISOString().split('T')[0]
          : "",
        domainId: selectedDomain?.id || "",
      });
    } else {
      resetForm();
    }
  }, [editEvent, securities, selectedDomain, resetForm]);

  // Gestionnaires
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    validateField(name, value);
  }, [validateField]);

  const handleTypeChange = useCallback((value) => {
    setFormData(prev => ({
      ...prev,
      type: value,
      amount: value !== "Dividend to be paid" ? "" : prev.amount,
      capital: value !== "Capital repayment" ? "" : prev.capital,
      nombreTitre: value !== "Non Cash" ? "" : prev.nombreTitre,
    }));
    
    setErrors(prev => ({
      ...prev,
      amount: "",
      capital: "",
      nombreTitre: "",
    }));
    
    validateField("type", value);
  }, [validateField, setErrors]);

  const handleSecurityChange = useCallback((selected) => {
    const securityId = selected?.id || "";
    setFormData(prev => ({ ...prev, securityId }));
    validateField("securityId", securityId);
  }, [validateField]);

  const closeModal = useCallback(() => {
    const modal = document.getElementById("newEvent");
    const instance = bootstrap.Modal.getInstance(modal);
    if (instance) instance.hide();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateAll()) {
      // toast.error("Veuillez corriger les erreurs et Ressayer.");
      return;
    }

    setLoading(true);
    showLoader({
      message: isEditMode ? "Mise à jour..." : "Création...",
      indeterminate: true,
    });

    try {
      const eventData = {
        domainId: parseInt(formData.domainId),
        type: formData.type,
        securityId: parseInt(formData.securityId),
        dateDePlanification: new Date(formData.dateDePlanification).toISOString().split('T')[0],
        amount: formData.type === "Dividend to be paid" ? parseFloat(formData.amount) : null,
        capital: formData.type === "Capital repayment" ? parseFloat(formData.capital) : null,
        nombreTitre: formData.type === "Non Cash" ? parseFloat(formData.nombreTitre) : null,
      };

      if (isEditMode) {
        await updateEvent(editEvent.id, eventData);
        toast.success("Événement mis à jour avec succès");
        onEventUpdated?.();
      } else {
        await createEvent(eventData);
        toast.success("Événement créé avec succès");
        onEventCreated?.();
      }

      resetForm();
      closeModal();
    } catch (error) {
      // console.error("Erreur:", error);
      toast.error(error.message || "Desole, Une erreur s'est produite");
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  // Déterminer quels champs afficher
  const showAmount = formData.type === "Dividend to be paid";
  const showCapital = formData.type === "Capital repayment";
  const showNombreTitre = formData.type === "Non Cash";

  return (
    <div className="modal fade" id="newEvent" tabIndex="-1" aria-hidden="true">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {isEditMode ? "✏️ Modifier l'OST" : "➕ Nouvelle Opération Sur Titre"}
            </h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal" onClick={resetForm}></button>
          </div>

          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <input type="hidden" name="domainId" value={formData.domainId} />

              <EventTypeSelector
                value={formData.type}
                onChange={handleTypeChange}
                error={errors.type}
              />

              {formData.type && (
                <div className="alert alert-info mb-3">
                  <strong>ℹ️ Instructions :</strong>
                  {showAmount && " Veuillez renseigner le montant du dividende."}
                  {showCapital && " Veuillez renseigner le pourcentage du capital."}
                  {showNombreTitre && " Veuillez renseigner le nombre de titres (valeur décimale acceptée)."}
                </div>
              )}

              {showAmount && (
                <ConditionalInput
                  type="amount"
                  label="Montant"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  error={errors.amount}
                  min="0.01"
                />
              )}

              {showCapital && (
                <ConditionalInput
                  type="capital"
                  label="Capital"
                  name="capital"
                  value={formData.capital}
                  onChange={handleChange}
                  error={errors.capital}
                  hint="(en %)"
                  icon="percent"
                  min="0"
                  max="100"
                />
              )}

              {showNombreTitre && (
                <ConditionalInput
                  type="nombreTitre"
                  label="Nombre de titres"
                  name="nombreTitre"
                  value={formData.nombreTitre}
                  onChange={handleChange}
                  error={errors.nombreTitre}
                  hint="(entre 0 et 1)"
                  min="0"
                  max="1"
                  step="0.01"
                />
              )}

              <SecurityTypeButtons value={securityType} onChange={setSecurityType} />

              <SecuritySelector
                securities={securities}
                value={formData.securityId}
                onChange={handleSecurityChange}
                error={errors.securityId}
                securityType={securityType}
              />

              <DateInput
                value={formData.dateDePlanification}
                onChange={handleChange}
                error={errors.dateDePlanification}
              />

              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                  onClick={resetForm}
                  disabled={loading}
                >
                  Annuler
                </button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      {isEditMode ? "Modification..." : "Création..."}
                    </>
                  ) : (
                    isEditMode ? "✓ Mettre à jour" : "✓ Créer l'OST"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FormulaireEvenement;